// GLSL inlined as TS string consts so we don't need a custom react-scripts
// loader. Keep these in sync with the .glsl source files in this folder
// (the .glsl files are reference / formatter targets only).

export const splatVertSrc = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;

in vec2 a_corner;
in int a_splatIndex;

uniform mat4 u_view;
uniform mat4 u_proj;
uniform vec2 u_viewport;
uniform float u_focal;
// 1 = orthographic, 0 = perspective. Set by adapter from projection matrix.
uniform int u_orthographic;
// Per-axis pixels-per-world-unit scale for ortho. Unused in perspective.
uniform vec2 u_orthoFocal;

uniform sampler2D u_splatData;
uniform int u_dataTexW;

out vec2 v_offset;
out vec3 v_color;
out float v_opacity;
out vec3 v_conic;

const float SH_C0 = 0.28209479177387814;

ivec2 dataCoord(int linearIdx) {
  return ivec2(linearIdx % u_dataTexW, linearIdx / u_dataTexW);
}

mat3 quatToMat(vec4 q) {
  float x = q.x, y = q.y, z = q.z, w = q.w;
  float xx = x * x, yy = y * y, zz = z * z;
  float xy = x * y, xz = x * z, yz = y * z;
  float wx = w * x, wy = w * y, wz = w * z;
  return mat3(
    1.0 - 2.0 * (yy + zz), 2.0 * (xy + wz),       2.0 * (xz - wy),
    2.0 * (xy - wz),       1.0 - 2.0 * (xx + zz), 2.0 * (yz + wx),
    2.0 * (xz + wy),       2.0 * (yz - wx),       1.0 - 2.0 * (xx + yy)
  );
}

void main() {
  int base = a_splatIndex * 4;
  vec4 d0 = texelFetch(u_splatData, dataCoord(base + 0), 0);
  vec4 d1 = texelFetch(u_splatData, dataCoord(base + 1), 0);
  vec4 d2 = texelFetch(u_splatData, dataCoord(base + 2), 0);
  vec4 d3 = texelFetch(u_splatData, dataCoord(base + 3), 0);

  vec3 a_position = d0.xyz;
  vec3 a_shDc = d1.xyz;
  float a_opacity = d1.w;
  vec3 a_scale = d2.xyz;
  // PLY rot_0..rot_3 = (w, x, y, z) per INRIA 3DGS convention; quatToMat
  // expects (x, y, z, w) so swap here.
  vec4 q = normalize(vec4(d3.yzw, d3.x));
  mat3 R = quatToMat(q);

  vec3 s = exp(a_scale);
  mat3 S = mat3(s.x, 0.0, 0.0, 0.0, s.y, 0.0, 0.0, 0.0, s.z);
  mat3 M = R * S;
  mat3 Sigma = M * transpose(M);

  vec4 vp = u_view * vec4(a_position, 1.0);
  // Perspective-only cull (guards the 1/vp.z EWA Jacobian). Orthographic has no
  // perspective divide, so vp.z >= 0 is valid geometry — culling it clips the
  // splat (e.g. at the bottom). See gsplatThreeAdapter ortho near/far refit.
  if (u_orthographic == 0 && vp.z >= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    v_color = vec3(0.0);
    v_opacity = 0.0;
    v_conic = vec3(0.0);
    v_offset = vec2(0.0);
    return;
  }

  mat3 W = mat3(u_view);
  mat3 T = W * Sigma * transpose(W);
  // EWA Jacobian. Perspective is z-dependent; ortho is constant.
  //   Perspective:  J = [[fx/z, 0, -fx*x/z²], [0, fy/z, -fy*y/z²], [0, 0, 0]]
  //   Orthographic: J = [[fx,   0, 0],         [0, fy,   0],         [0, 0, 0]]
  mat3 J;
  if (u_orthographic == 1) {
    J = mat3(
      u_orthoFocal.x, 0.0, 0.0,
      0.0, u_orthoFocal.y, 0.0,
      0.0, 0.0, 0.0
    );
  } else {
    float zinv = 1.0 / vp.z;
    float zinv2 = zinv * zinv;
    float fx = u_focal;
    float fy = u_focal;
    J = mat3(
      fx * zinv, 0.0, 0.0,
      0.0, fy * zinv, 0.0,
      -fx * vp.x * zinv2, -fy * vp.y * zinv2, 0.0
    );
  }
  mat3 cov2 = J * T * transpose(J);
  // INRIA-style anti-aliasing low-pass: +0.3 px² so very-flat splats still
  // cover ~0.55 px. Same in both modes — bumping this for ortho drowns the
  // off-diagonal term and turns anisotropic splats into perfect circles.
  float detOrig = cov2[0][0] * cov2[1][1] - cov2[0][1] * cov2[0][1];
  float a = cov2[0][0] + 0.3;
  float b = cov2[0][1];
  float c = cov2[1][1] + 0.3;
  // AA energy compensation (matches PlayCanvas GSPLAT_AA). The +0.3 blur enlarges
  // the gaussian; scale opacity by sqrt(det_orig/det_blur) <= 1 so small splats —
  // which carry fine surface detail, where 0.3 px² is large relative to their own
  // covariance — don't come out both fatter and too opaque and wash out detail.
  float detBlur = a * c - b * b;
  float aaFactor = detBlur > 0.0 ? sqrt(max(detOrig / detBlur, 0.0)) : 1.0;
  // Ortho-only min-radius enforcement: scale (a,b,c) uniformly so the
  // SMALLER eigenvalue of cov2 reaches MIN_R² px² (~1.4 px radius). Uniform
  // scale preserves the eigenvalue ratio — anisotropic splats stay
  // anisotropic, just enlarged. Perspective doesn't need this: the fx/z
  // factor in J already grows near-camera splats to visible pixel sizes.
  if (u_orthographic == 1) {
    float mid  = 0.5 * (a + c);
    float det0 = a * c - b * b;
    float disc = sqrt(max(0.0, mid * mid - det0));
    float lamMin = mid - disc;
    const float MIN_R_SQR = 2.0;
    if (lamMin > 0.0 && lamMin < MIN_R_SQR) {
      float s = MIN_R_SQR / lamMin;
      a *= s; b *= s; c *= s;
    }
  }

  float det = a * c - b * b;
  if (det <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    v_color = vec3(0.0); v_opacity = 0.0; v_conic = vec3(0.0); v_offset = vec2(0.0);
    return;
  }
  float invDet = 1.0 / det;
  vec3 conic = vec3(c * invDet, -b * invDet, a * invDet);

  float mid = 0.5 * (a + c);
  float disc = sqrt(max(0.1, mid * mid - det));
  float lam1 = mid + disc;
  float lam2 = max(mid - disc, 0.1);
  float r1 = sqrt(lam1);
  float r2 = sqrt(lam2);
  vec2 axis1 = normalize(vec2(b, lam1 - a));
  if (length(vec2(b, lam1 - a)) < 1e-6) axis1 = vec2(1.0, 0.0);
  vec2 axis2 = vec2(-axis1.y, axis1.x);

  // Draw each splat to sqrt(8) sigma and normalise the falloff to hit exactly
  // zero at that edge (see frag shader). 3.0 sigma left a faint soft tail that
  // made neighbouring splats bleed together — a smeary, oil-paint look that
  // lost fine surface texture. sqrt(8) + edge-normalised falloff matches
  // PlayCanvas's normExp and keeps splats tight, so detail stays crisp.
  float kSigma = 2.8284271;
  // Discard explosive splats (degenerate gaussians or huge ones near the
  // camera). Visible diameter is 2·kSigma·r1, so cap radius at 12.5% of the
  // shorter viewport dim — anything wider than 25% of the screen is junk.
  // Clamp oversized splats to vmin instead of discarding them (matches
  // PlayCanvas). Dropping big near/rim splats left holes and smears; clamping the
  // radius keeps them drawn but bounded to a fraction of the screen.
  float vmin = min(1024.0, min(u_viewport.x, u_viewport.y));
  float cap = vmin / kSigma;
  r1 = min(r1, cap);
  r2 = min(r2, cap);
  vec2 offsetPix = a_corner.x * (kSigma * r1) * axis1 + a_corner.y * (kSigma * r2) * axis2;

  vec4 clip = u_proj * vp;
  vec2 ndcOffset = (offsetPix / u_viewport) * 2.0 * clip.w;
  clip.xy += ndcOffset;
  gl_Position = clip;

  v_offset = offsetPix;
  v_color = SH_C0 * a_shDc + 0.5;
  v_opacity = (1.0 / (1.0 + exp(-a_opacity))) * aaFactor;
  v_conic = conic;
}
`;

export const splatFragSrc = /* glsl */ `#version 300 es
precision highp float;

in vec2 v_offset;
in vec3 v_color;
in float v_opacity;
in vec3 v_conic;

out vec4 fragColor;

void main() {
  float x = v_offset.x;
  float y = v_offset.y;
  float power = -0.5 * (v_conic.x * x * x + v_conic.z * y * y) - v_conic.y * x * y;
  if (power > 0.0) discard;
  // Edge-normalised gaussian: subtract the value at the sqrt(8)-sigma quad edge
  // (exp(-4)) and rescale, so the splat fades to exactly zero at its boundary
  // instead of cutting off a faint soft tail. This is PlayCanvas's normExp and
  // stops splats bleeding into each other (the oil-paint smear).
  float alpha = v_opacity * max(exp(power) - 0.01831564, 0.0) * 1.0186512;
  if (alpha < 1.0 / 255.0) discard;
  fragColor = vec4(v_color * alpha, alpha);
}
`;
